<template>
  <v-card
    v-if="control.visible"
    class="additional-properties-card"
    v-bind="vuetifyProps('v-card')"
    flat
  >
    <v-container class="py-0">
      <div
        class="additional-properties-add"
        :class="{
          'additional-properties-add--with-title':
            mdAndUp && additionalPropertiesTitle,
        }"
      >
        <div
          v-if="mdAndUp && additionalPropertiesTitle"
          class="additional-properties-title"
        >
          {{ additionalPropertiesTitle }}
        </div>
        <div class="additional-properties-add-field">
          <json-forms
            :data="newPropertyName"
            :uischema="
              {
                type: 'Control',
                scope: '#',
                label: propertyNameLabel,
              } as UISchemaElement
            "
            :schema="propertyNameSchema"
            :additionalErrors="additionalErrors"
            :renderers="control.renderers"
            :cells="control.cells"
            :config="control.config"
            :readonly="!isControlEditable(control)"
            :validation-mode="validationMode"
            :i18n="i18n"
            :ajv="ajv"
            :middleware="middleware"
            @change="propertyNameChange"
          ></json-forms>
        </div>
        <v-tooltip location="bottom">
          <template v-slot:activator="{ props }">
            <v-btn
              icon
              variant="text"
              elevation="0"
              small
              :aria-label="translations.addAriaLabel"
              v-bind="props"
              :disabled="addPropertyDisabled"
              @click="addProperty"
            >
              <v-icon>{{ icons.current.value.itemAdd }}</v-icon>
            </v-btn>
          </template>
          {{ translations.addTooltip }}
        </v-tooltip>
      </div>
    </v-container>
    <v-container
      v-bind="vuetifyProps('v-container')"
      class="additional-properties-items py-0"
    >
      <div
        class="additional-property-row"
        v-for="element in additionalPropertyItems"
        :key="`${element.propertyName}`"
      >
        <div class="additional-property-content">
          <json-forms
            v-if="
              element.propertyName.includes('.') || element.propertyName === ''
            "
            :data="control.data[element.propertyName]"
            :schema="literalValueSchema(element.schema)"
            :uischema="
              {
                type: 'Control',
                scope: '#',
                label: element.propertyName,
              } as UISchemaElement
            "
            :renderers="control.renderers"
            :cells="control.cells"
            :config="control.config"
            :readonly="!isControlEditable(control)"
            :i18n="i18n"
            :ajv="ajv"
            :validation-mode="parentValidationMode"
            @change="literalValueChange(element.propertyName, $event)"
          />
          <dispatch-renderer
            v-else-if="element.schema && element.uischema"
            :schema="element.schema"
            :uischema="element.uischema"
            :path="element.path"
            :enabled="control.enabled"
            :readonly="control.readonly"
            :renderers="control.renderers"
            :cells="control.cells"
          />
        </div>
        <div v-if="control.enabled" class="additional-property-actions">
          <v-menu
            :model-value="renamingPropertyName === element.propertyName"
            :close-on-content-click="false"
            location="top end"
            @update:model-value="
              (open: boolean) =>
                open ? startRename(element.propertyName) : cancelRename()
            "
          >
            <template v-slot:activator="{ props }">
              <v-btn
                v-bind="props"
                class="additional-property-action-button"
                icon
                variant="text"
                elevation="0"
                :disabled="!canRenameProperty(element.propertyName)"
                :aria-label="renamePropertyAriaLabel(element.propertyName)"
                :title="renamePropertyAriaLabel(element.propertyName)"
              >
                <v-icon class="notranslate">{{
                  icons.current.value.itemEdit
                }}</v-icon>
              </v-btn>
            </template>
            <v-card class="additional-property-rename-menu" elevation="8">
              <v-text-field
                v-model="renameValue"
                density="compact"
                hide-details="auto"
                autofocus
                :label="propertyNameLabel"
                :error-messages="renameError ? [renameError] : []"
                v-bind="vuetifyProps('v-text-field')"
                @update:model-value="updateRenameError(element.propertyName)"
                @keydown.enter="renameProperty(element.propertyName)"
                @keydown.esc="cancelRename"
              />
              <div class="additional-property-rename-actions">
                <v-btn
                  variant="text"
                  size="small"
                  :disabled="renamePropertyDisabled(element.propertyName)"
                  @click="renameProperty(element.propertyName)"
                >
                  {{ translations.renameConfirm }}
                </v-btn>
                <v-btn variant="text" size="small" @click="cancelRename">
                  {{ translations.cancel }}
                </v-btn>
              </div>
            </v-card>
          </v-menu>
          <v-tooltip location="bottom">
            <template v-slot:activator="{ props }">
              <v-btn
                v-bind="props"
                class="additional-property-action-button"
                icon
                variant="text"
                elevation="0"
                :aria-label="translations.removeAriaLabel"
                :disabled="
                  !canRemoveProperty(element.propertyName) ||
                  renamingPropertyName === element.propertyName
                "
                @click="removeProperty(element.propertyName)"
              >
                <v-icon class="notranslate">{{
                  icons.current.value.itemDelete
                }}</v-icon>
              </v-btn>
            </template>
            {{ translations.removeTooltip }}
          </v-tooltip>
        </div>
      </div>
    </v-container>
  </v-card>
</template>

<script lang="ts">
import { AdditionalPropertiesTranslationEnum } from '@/i18n';
import { additionalPropertiesDefaultTranslations } from '@/i18n/additionalPropertiesTranslations';
import {
  getAdditionalPropertiesTranslations,
  getAdditionalPropertyTranslation,
} from '@/i18n/i18nUtil';
import {
  Generate,
  createControlElement,
  createDefaultValue,
  getI18nKeyPrefix,
  type GroupLayout,
  type JsonSchema,
  type JsonSchema7,
  type UISchemaElement,
} from '@jsonforms/core';
import {
  DispatchRenderer,
  JsonForms,
  useAjv,
  useJsonForms,
  useJsonFormsControlWithDetail,
  useTranslator,
  type JsonFormsChangeEvent,
} from '@jsonforms/vue';
import type { ErrorObject } from 'ajv';
import get from 'lodash/get';
import isEqual from 'lodash/isEqual';
import isPlainObject from 'lodash/isPlainObject';
import startCase from 'lodash/startCase';

import { literalPropertySchema } from '../../util/literalPropertySchema';
import { IsDynamicPropertyContext } from '@/util/inject';
import {
  computed,
  defineComponent,
  markRaw,
  provide,
  ref,
  type PropType,
  type DefineComponent,
} from 'vue';
import { useDisplay } from 'vuetify';
import {
  VBtn,
  VCard,
  VContainer,
  VIcon,
  VMenu,
  VTextField,
  VTooltip,
} from 'vuetify/components';
import { DisabledIconFocus } from '../../controls/directives';
import { useStyles } from '../../styles';
import {
  canRenameDynamicProperty,
  composePropertyPath,
  findPropertySchema,
  getDynamicPropertyNameErrorMessage,
  getPropertyNameSchema,
  validateDynamicPropertyName,
} from '../../util/dynamicProperties';
import {
  isControlEditable,
  useControlAppliedOptions,
  useIcons,
} from '../../util';

type Input = ReturnType<typeof useJsonFormsControlWithDetail>;
export interface AdditionalPropertyType {
  propertyName: string;
  path: string;
  schema: JsonSchema;
  uischema: UISchemaElement | undefined;
}

export default defineComponent({
  name: 'additional-properties',
  components: {
    DispatchRenderer,
    VCard,
    VTooltip,
    VIcon,
    VBtn,
    VMenu,
    VContainer,
    VTextField,
    JsonForms,
  },
  directives: {
    DisabledIconFocus,
  },
  props: {
    input: {
      type: Object as PropType<Input>,
      required: true,
    },
  },
  setup(props) {
    // eslint-disable-next-line vue/no-setup-props-destructure
    const control = props.input.control;
    const reservedPropertyNames = computed(() =>
      Object.keys(control.value.schema.properties || {}),
    );

    const additionalKeys = computed(() =>
      Object.keys(control.value.data || {}).filter(
        (k) => !reservedPropertyNames.value.includes(k),
      ),
    );

    const toAdditionalPropertyType = (
      propName: string,
      parentSchema: JsonSchema,
      rootSchema: JsonSchema,
    ): AdditionalPropertyType => {
      let propSchema = findPropertySchema(parentSchema, propName, rootSchema);
      let propUiSchema: UISchemaElement | undefined = undefined;

      propSchema = propSchema ?? {};

      if (
        propSchema.type === undefined &&
        !propSchema.allOf &&
        !propSchema.anyOf &&
        !propSchema.oneOf
      ) {
        propSchema = {
          ...propSchema,
          type: [
            'array',
            'boolean',
            'integer',
            'null',
            'number',
            'object',
            'string',
          ],
        };
      }

      if (propSchema.type === 'array') {
        propUiSchema = Generate.uiSchema(
          propSchema,
          'Group',
          undefined,
          control.value.rootSchema,
        );
        (propUiSchema as GroupLayout).label =
          propSchema.title ?? startCase(propName);
      } else {
        propUiSchema = createControlElement('#');
      }

      propSchema = {
        ...propSchema,
        title: propName,
      };
      if (propSchema.type === 'object') {
        propSchema.additionalProperties =
          propSchema.additionalProperties !== false
            ? (propSchema.additionalProperties ?? true)
            : false;
      } else if (propSchema.type === 'array') {
        propSchema.items = propSchema.items ?? {};
      }

      return {
        propertyName: propName,
        path: composePropertyPath(control.value.path, propName),
        schema: propSchema,
        uischema: propUiSchema,
      };
    };

    const appliedOptions = useControlAppliedOptions(props.input);
    const additionalPropertyItems = computed<AdditionalPropertyType[]>(() =>
      additionalKeys.value.map((propName) =>
        toAdditionalPropertyType(
          propName,
          control.value.schema,
          control.value.rootSchema,
        ),
      ),
    );

    const styles = useStyles(control.value.uischema);
    const newPropertyName = ref<string | null>('');
    const newPropertyErrors = ref<ErrorObject[] | undefined>(undefined);
    const additionalErrors = ref<ErrorObject[]>([]);
    const renamingPropertyName = ref<string | null>(null);
    const renameValue = ref('');
    let renameTarget: unknown;
    let renameTargetPath: string | undefined;
    const captureRenameTarget = () => {
      renameTarget = control.value.data;
      renameTargetPath = control.value.path;
    };
    const isRenameTargetCurrent = () =>
      renameTarget === control.value.data &&
      renameTargetPath === control.value.path;
    const renameError = ref<string | null>(null);

    const propertyNameSchema = computed<JsonSchema7>(() =>
      getPropertyNameSchema(control.value.schema, control.value.rootSchema),
    );

    const propertyNameChange = (event: JsonFormsChangeEvent) => {
      newPropertyName.value = typeof event.data === 'string' ? event.data : '';
      const validationError = validateDynamicPropertyName({
        allowEmptyPropertyNames:
          appliedOptions.value.allowEmptyPropertyNames === true,
        allowDots: true,
        propertyName: newPropertyName.value,
        reservedPropertyNames: reservedPropertyNames.value,
        data: control.value.data,
      });
      const message = getDynamicPropertyNameErrorMessage(validationError, {
        alreadyDefined: translatePropertyName(
          AdditionalPropertiesTranslationEnum.propertyAlreadyDefined,
          newPropertyName.value,
        ),
        invalid: translatePropertyName(
          AdditionalPropertiesTranslationEnum.propertyNameInvalid,
          newPropertyName.value,
        ),
      });
      // Additional errors remain visible even in ValidateAndHide mode. Keep
      // the initial/reset empty draft quiet; Add validates the name separately.
      const newAdditionalErrors: ErrorObject[] =
        message && newPropertyName.value !== ''
          ? [
              {
                data: newPropertyName.value,
                instancePath: '',
                keyword: '',
                message,
                params: { propertyName: newPropertyName.value },
                schemaPath: '',
              },
            ]
          : [];

      if (!isEqual(additionalErrors.value, newAdditionalErrors)) {
        // only change the additional errors if different to prevent recursive calls
        additionalErrors.value = newAdditionalErrors;
      }
      newPropertyErrors.value = [...(event.errors ?? [])];
    };

    const vuetifyProps = (path: string) => {
      const props = get(appliedOptions.value?.vuetify, path);

      return props && isPlainObject(props) ? props : {};
    };

    const icons = useIcons();
    const t = useTranslator();

    const i18nAdditionalPropertiesPrefix = getI18nKeyPrefix(
      control.value.schema,
      control.value.uischema,
      control.value.path + '.additionalProperties',
    );

    const translations = getAdditionalPropertiesTranslations(
      t.value,
      additionalPropertiesDefaultTranslations,
      i18nAdditionalPropertiesPrefix,
      control.value.label,
      newPropertyName,
    );
    const translatePropertyName = (
      key: AdditionalPropertiesTranslationEnum,
      propertyName: string,
    ) =>
      getAdditionalPropertyTranslation(
        t.value,
        additionalPropertiesDefaultTranslations,
        i18nAdditionalPropertiesPrefix,
        key,
        propertyName,
      );
    const renamePropertyAriaLabel = (propertyName: string) =>
      translatePropertyName(
        AdditionalPropertiesTranslationEnum.renameAriaLabel,
        propertyName,
      );

    const propertyNameLabel =
      translations[AdditionalPropertiesTranslationEnum.propertyNameLabel];

    const { mdAndUp } = useDisplay();

    const jsonforms = useJsonForms();
    const {
      validationMode: parentValidationMode,
      i18n,
      middleware,
    } = jsonforms;
    const ajv = useAjv();
    const translatePropertyNameSchemaError = (error: ErrorObject) =>
      jsonforms.i18n?.translateError?.(
        error,
        t.value,
        control.value.uischema,
      ) ??
      error.message ??
      translatePropertyName(
        AdditionalPropertiesTranslationEnum.propertyNameInvalid,
        renameValue.value,
      );

    // if the new property name is not specified then hide any errors
    const validationMode = computed(() =>
      newPropertyName.value ? parentValidationMode : 'ValidateAndHide',
    );

    // use the default value since all properties are dynamic so preserve the property key
    provide(IsDynamicPropertyContext, true);

    const literalValueSchema = (schema: JsonSchema) =>
      literalPropertySchema(schema, control.value.rootSchema);

    return {
      literalValueSchema,
      parentValidationMode,
      validationMode: validationMode,
      i18n: i18n ? markRaw(i18n) : i18n,
      middleware: middleware ? markRaw(middleware) : middleware,
      t,
      mdAndUp,
      vuetifyProps,
      ajv: ajv ? markRaw(ajv) : ajv,
      control,
      styles,
      appliedOptions,
      additionalPropertyItems,
      reservedPropertyNames,
      toAdditionalPropertyType,
      additionalKeys,
      newPropertyName,
      propertyNameLabel,
      propertyNameChange,
      newPropertyErrors,
      additionalErrors,
      renamingPropertyName,
      renameValue,
      captureRenameTarget,
      isRenameTargetCurrent,
      renameError,
      icons,
      isControlEditable,
      canRenameDynamicProperty,
      propertyNameSchema,
      translations,
      translatePropertyName,
      renamePropertyAriaLabel,
      translatePropertyNameSchemaError,
    };
  },
  computed: {
    addPropertyDisabled(): boolean {
      return (
        // add is disabled because the overall control is disabled
        !this.isControlEditable(this.control) ||
        // add is disabled because of contraints
        (this.appliedOptions.restrict && this.maxPropertiesReached) ||
        // Empty strings are valid names; validate them like any other key.
        (this.newPropertyErrors && this.newPropertyErrors.length > 0) ||
        (this.additionalErrors && this.additionalErrors.length > 0) ||
        this.newPropertyName === null ||
        Boolean(this.validatePropertyName(this.newPropertyName))
      );
    },
    maxPropertiesReached(): boolean {
      return (
        this.control.schema.maxProperties !== undefined && // we have maxProperties constraint
        this.control.data && // we have data to check
        // the current number of properties in the object is greater or equals to the maxProperties
        Object.keys(this.control.data).length >=
          this.control.schema.maxProperties
      );
    },
    minPropertiesReached(): boolean {
      return (
        this.control.schema.minProperties !== undefined && // we have minProperties constraint
        this.control.data && // we have data to check
        // the current number of properties in the object is less or equals to the minProperties
        Object.keys(this.control.data).length <=
          this.control.schema.minProperties
      );
    },
    additionalPropertiesTitle(): string | undefined {
      const title = (this.control.schema.additionalProperties as JsonSchema7)
        ?.title;
      return title ? this.t(title, title) : title;
    },
  },
  watch: {
    'control.data'() {
      this.cancelRename();
    },
    'control.path'() {
      this.cancelRename();
    },
  },
  methods: {
    literalValueChange(
      propertyName: string,
      event: JsonFormsChangeEvent,
    ): void {
      const data = this.control.data;
      if (
        !this.isControlEditable(this.control) ||
        !data ||
        typeof data !== 'object' ||
        Array.isArray(data) ||
        !Object.prototype.hasOwnProperty.call(data, propertyName) ||
        isEqual(data[propertyName], event.data)
      )
        return;
      // Write at the parent path; the literal key must never be parsed as a path.
      this.input.handleChange(this.control.path, {
        ...data,
        [propertyName]: event.data,
      });
    },
    validatePropertyName(
      propertyName: string,
      currentPropertyName?: string,
    ): string | null {
      return getDynamicPropertyNameErrorMessage(
        validateDynamicPropertyName({
          allowEmptyPropertyNames:
            this.appliedOptions.allowEmptyPropertyNames === true,
          allowDots: true,
          propertyName,
          currentPropertyName,
          reservedPropertyNames: this.reservedPropertyNames,
          data: this.control.data,
          propertyNameSchema: this.propertyNameSchema,
          ajv: this.ajv,
        }),
        {
          alreadyDefined: this.translatePropertyName(
            AdditionalPropertiesTranslationEnum.propertyAlreadyDefined,
            propertyName,
          ),
          invalid: this.translatePropertyName(
            AdditionalPropertiesTranslationEnum.propertyNameInvalid,
            propertyName,
          ),
          schema: this.translatePropertyNameSchemaError,
        },
      );
    },
    canRenameProperty(propName: string): boolean {
      return this.canRenameDynamicProperty({
        schema: this.control.schema,
        data: this.control.data,
        propertyName: propName,
        enabled: this.control.enabled,
        readonly: this.control.readonly,
        restrict: this.appliedOptions.restrict,
      });
    },
    startRename(propName: string): void {
      if (!this.canRenameProperty(propName)) return;
      this.captureRenameTarget();
      this.renamingPropertyName = propName;
      this.renameValue = propName;
      this.renameError = null;
    },
    cancelRename(): void {
      this.renamingPropertyName = null;
      this.renameValue = '';
      this.renameError = null;
    },
    renamePropertyDisabled(propName: string): boolean {
      const name = this.renameValue;
      return (
        !this.canRenameProperty(propName) ||
        name === propName ||
        Boolean(this.validatePropertyName(name, propName))
      );
    },
    updateRenameError(propName: string): void {
      this.renameError = this.validatePropertyName(this.renameValue, propName);
    },
    renameProperty(propName: string): void {
      if (
        this.renamingPropertyName !== propName ||
        !this.isRenameTargetCurrent() ||
        !this.canRenameProperty(propName)
      ) {
        this.cancelRename();
        return;
      }
      const name = this.renameValue;
      this.renameError = this.validatePropertyName(name, propName);
      if (
        this.renameError ||
        name === propName ||
        typeof this.control.data !== 'object' ||
        this.control.data === null ||
        Array.isArray(this.control.data)
      ) {
        return;
      }

      const updatedData = Object.fromEntries(
        Object.entries(this.control.data).map(([key, value]) => [
          key === propName ? name : key,
          value,
        ]),
      );
      this.input.handleChange(this.control.path, updatedData);
      this.cancelRename();
    },
    addProperty() {
      if (
        !this.isControlEditable(this.control) ||
        (this.appliedOptions.restrict && this.maxPropertiesReached) ||
        this.newPropertyName === null ||
        this.validatePropertyName(this.newPropertyName)
      )
        return;
      const data = this.control.data;
      if (data != null && (typeof data !== 'object' || Array.isArray(data)))
        return;
      const additionalProperty = this.toAdditionalPropertyType(
        this.newPropertyName,
        this.control.schema,
        this.control.rootSchema,
      );
      const updatedData = { ...data };
      updatedData[this.newPropertyName] = createDefaultValue(
        additionalProperty.schema,
        this.control.rootSchema,
      );
      this.input.handleChange(this.control.path, updatedData);
      this.newPropertyName = '';
    },
    canRemoveProperty(propName: string): boolean {
      return (
        this.canRenameProperty(propName) &&
        !(this.appliedOptions.restrict && this.minPropertiesReached)
      );
    },
    removeProperty(propName: string): void {
      if (!this.canRemoveProperty(propName)) return;
      const updatedData = { ...this.control.data };
      delete updatedData[propName];
      this.input.handleChange(this.control.path, updatedData);
    },
  },
}) as DefineComponent<any, any, any>;
</script>

<style scoped>
.additional-properties-card {
  margin-top: 8px;
}

.additional-properties-items {
  width: 100%;
}

.additional-properties-add {
  align-items: flex-start;
  display: grid;
  gap: 8px;
  grid-template-columns: minmax(0, 1fr) auto;
  width: 100%;
}

.additional-properties-add--with-title {
  grid-template-columns: minmax(180px, max-content) minmax(0, 1fr) auto;
}

.additional-properties-title {
  padding-top: 10px;
}

.additional-properties-add-field {
  min-width: 0;
}

.additional-property-row {
  align-items: flex-start;
  display: grid;
  gap: 8px;
  grid-template-columns: minmax(0, 1fr) auto;
  min-width: 0;
  width: 100%;
}

.additional-property-content {
  min-width: 0;
  width: 100%;
}

.additional-property-actions {
  align-items: center;
  display: inline-flex;
  flex-direction: column;
  gap: 0;
  opacity: 0.72;
  transition: opacity 120ms ease;
}

.additional-property-row:hover .additional-property-actions {
  opacity: 1;
}

.additional-property-action-button {
  height: 24px;
  width: 24px;
}

.additional-property-action-button :deep(.v-icon) {
  font-size: 16px;
}

.additional-property-rename-menu {
  min-width: 280px;
  padding: 12px;
}

.additional-property-rename-actions {
  align-items: flex-start;
  display: flex;
  gap: 8px;
  justify-content: flex-end;
  margin-top: 8px;
}
</style>
